import { AlertTriangle, Bot, CheckCircle2, FileUp, Loader2, Upload, Zap } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { FormModal } from "@/components/shared/FormModal";
import { useToast } from "@/components/shared/Toast";
import { getApiErrorMessage } from "@/lib";
import { skillService } from "@/lib/services";
import type { BundleAnalysis, SkillImportResponse, SkillImportSelection } from "@/types";

interface ImportSkillModalProps {
  onClose: () => void;
  /** Called after a successful import (createdCount > 0) so the caller can refresh. */
  onImported?: (result: SkillImportResponse) => void;
  /** Modal title — the same bundle may create skills and/or agents, so callers
   *  can frame it contextually (e.g. "Import Agent" on the Agents page). */
  title?: string;
}

type Step = "upload" | "preview";

// UI selection state: which skills/agents are checked, plus agent name overrides.
interface Selection {
  skills: Record<string, boolean>; // sourcePath -> checked
  agents: Record<string, boolean>; // sourceFile -> checked
  agentNames: Record<string, string>; // sourceFile -> name
}

/**
 * Import a zipped Agent-Skills bundle (SKILL.md + references/scripts + agents/).
 * Three steps: upload → review (uncheck items, rename agents) → approve. Creates
 * private, user-owned skills and (from agents/ configs) private agents (MHS-359).
 */
export function ImportSkillModal({ onClose, onImported, title = "Import Skill" }: Readonly<ImportSkillModalProps>) {
  const { showToast } = useToast();
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState<BundleAnalysis | null>(null);
  const [selection, setSelection] = useState<Selection>({ skills: {}, agents: {}, agentNames: {} });

  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      showToast("Please select a .zip bundle", "error");
      return;
    }
    setFile(f);
  };

  const analyze = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || busy) return;
    try {
      setBusy(true);
      const res = await skillService.analyzeBundle(file);
      const data = res.data;
      // Default: everything selected.
      const sel: Selection = { skills: {}, agents: {}, agentNames: {} };
      for (const s of data.skills) {
        sel.skills[s.sourcePath] = !s.nameTaken; // don't preselect a duplicate name
        for (const a of s.agents) {
          sel.agents[a.sourceFile] = true;
          sel.agentNames[a.sourceFile] = a.name;
        }
      }
      setAnalysis(data);
      setSelection(sel);
      setStep("preview");
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to read bundle"), "error");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!file || !analysis || busy) return;
    const payload: SkillImportSelection = {
      skillPaths: analysis.skills.filter((s) => selection.skills[s.sourcePath]).map((s) => s.sourcePath),
      agentFiles: analysis.skills
        .flatMap((s) => s.agents)
        .filter((a) => selection.agents[a.sourceFile])
        .map((a) => a.sourceFile),
      agentNames: selection.agentNames,
    };
    if (payload.skillPaths?.length === 0) {
      showToast("Select at least one skill to import", "error");
      return;
    }
    try {
      setBusy(true);
      const res = await skillService.importBundle(file, payload);
      if (res.data.createdCount > 0) {
        const agents = res.data.createdAgentCount ?? 0;
        const suffix = agents > 0 ? `, ${agents} agent(s)` : "";
        showToast(`Imported ${res.data.createdCount} skill(s)${suffix}`, "success");
        onImported?.(res.data);
        onClose();
      } else {
        // Nothing created (e.g. duplicate names) — stay open so the user can adjust.
        showToast("No skills were imported", "error");
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to import"), "error");
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-content-tertiary hover:text-content-primary transition"
      >
        Cancel
      </button>
      {step === "upload" && (
        <button
          type="submit"
          disabled={!file || busy}
          className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Continue
        </button>
      )}
      {step === "preview" && (
        <button
          type="button"
          onClick={approve}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Import selected
        </button>
      )}
    </div>
  );

  return (
    <FormModal title={title} onClose={onClose} onSubmit={analyze} footer={footer}>
      {step === "upload" && <UploadStep file={file} dragging={dragging} setDragging={setDragging} onPick={pickFile} />}
      {step === "preview" && analysis && (
        <PreviewStep analysis={analysis} selection={selection} setSelection={setSelection} />
      )}
    </FormModal>
  );
}

function UploadStep({
  file,
  dragging,
  setDragging,
  onPick,
}: Readonly<{
  file: File | null;
  dragging: boolean;
  setDragging: (v: boolean) => void;
  onPick: (f: File | undefined) => void;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <p className="text-sm text-content-tertiary">
        Upload a <code className="font-mono">.zip</code> containing one or more bundles. Each bundle is a folder with a{" "}
        <code className="font-mono">SKILL.md</code> and optional <code className="font-mono">references/</code>,{" "}
        <code className="font-mono">scripts/</code>, and <code className="font-mono">agents/</code> subfolders. You'll
        review what was found before anything is created.
      </p>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          onPick(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition ${
          dragging ? "border-brand-primary bg-brand-primary-subtle" : "border-border-primary"
        }`}
      >
        <FileUp className="w-8 h-8 text-content-quaternary" />
        {file ? (
          <p className="text-sm text-content-primary font-medium">{file.name}</p>
        ) : (
          <p className="text-sm text-content-tertiary">Drag a .zip here, or</p>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          browse files
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
        />
      </div>
    </>
  );
}

function PreviewStep({
  analysis,
  selection,
  setSelection,
}: Readonly<{
  analysis: BundleAnalysis;
  selection: Selection;
  setSelection: React.Dispatch<React.SetStateAction<Selection>>;
}>) {
  const toggleSkill = (path: string) =>
    setSelection((s) => ({ ...s, skills: { ...s.skills, [path]: !s.skills[path] } }));
  const toggleAgent = (file: string) =>
    setSelection((s) => ({ ...s, agents: { ...s.agents, [file]: !s.agents[file] } }));
  const renameAgent = (file: string, name: string) =>
    setSelection((s) => ({ ...s, agentNames: { ...s.agentNames, [file]: name } }));

  return (
    <div className="space-y-3">
      <p className="text-sm text-content-tertiary">
        Select what to import. Uncheck anything you don't want; rename agents before creating them.
      </p>
      {analysis.skills.map((skill) => (
        <div key={skill.sourcePath} className="rounded-lg border border-border-primary p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!selection.skills[skill.sourcePath]}
              onChange={() => toggleSkill(skill.sourcePath)}
              className="mt-1"
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                <span className="text-sm font-medium text-content-primary">{skill.name}</span>
                {skill.nameTaken && (
                  <span className="text-[10px] uppercase tracking-wide text-amber-400">name taken</span>
                )}
                {skill.usable === false && (
                  <span className="text-[10px] uppercase tracking-wide text-red-400">won't be usable</span>
                )}
              </span>
              {skill.description && (
                <span className="block text-xs text-content-tertiary mt-0.5">{skill.description}</span>
              )}
              {skill.missingTools.length > 0 && (
                <span className="block text-xs text-amber-400 mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Requires tools not installed on this system: {skill.missingTools.join(", ")}. The skill will be hidden
                  from agents until they're added.
                </span>
              )}
              {(skill.disabledTools?.length ?? 0) > 0 && (
                <span className="block text-xs text-amber-400 mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Requires tools that are currently disabled: {skill.disabledTools.join(", ")}. Enable them so agents
                  can use this skill.
                </span>
              )}
              {skill.requiresExecution && (
                <span className="block text-xs text-amber-400 mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    This skill ships {skill.scriptCount} script{skill.scriptCount === 1 ? "" : "s"} that need a
                    code-execution environment. It will import as instructions only — the scripts won't run here. For
                    security, scripts must be reviewed and installed by a system admin: please request them through
                    Feedback if you need them enabled.
                  </span>
                </span>
              )}
              {!skill.requiresExecution && (skill.scriptCount > 0 || skill.referenceCount > 0) && (
                <span className="block text-xs text-content-quaternary mt-1">
                  Bundles{" "}
                  {skill.scriptCount > 0 && (
                    <>
                      {skill.scriptCount} script{skill.scriptCount === 1 ? "" : "s"}
                    </>
                  )}
                  {skill.scriptCount > 0 && skill.referenceCount > 0 && " · "}
                  {skill.referenceCount > 0 && (
                    <>
                      {skill.referenceCount} reference{skill.referenceCount === 1 ? "" : "s"}
                    </>
                  )}
                </span>
              )}
            </span>
          </label>

          {skill.agents.length > 0 && (
            <div className="mt-2 ml-6 space-y-1.5 border-t border-border-secondary pt-2">
              <p className="text-xs text-content-tertiary">Agents in this bundle:</p>
              {skill.agents.map((agent) => (
                <div key={agent.sourceFile} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!selection.agents[agent.sourceFile]}
                    onChange={() => toggleAgent(agent.sourceFile)}
                  />
                  <Bot className="w-3.5 h-3.5 text-brand-secondary shrink-0" />
                  <input
                    value={selection.agentNames[agent.sourceFile] ?? agent.name}
                    onChange={(e) => renameAgent(agent.sourceFile, e.target.value)}
                    disabled={!selection.agents[agent.sourceFile]}
                    className="flex-1 text-sm bg-surface-secondary text-content-primary px-2 py-0.5 rounded border border-border-primary focus:outline-hidden focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                    maxLength={255}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
